// The ANVA back-office orders each product's coverages by an explicit per-coverage sequence field
// (XX_VOLGNUM) rather than JSON key order. For every product we assign a running volgnummer in a
// fixed order — main coverage first, then the remaining coverages in their on-screen order,
// including always-included hidden ones (e.g. Inboedel's Buitenhuis / Mobiele elektronica, and
// glass). Assigned at the document-finalize points (offer + final request) after all merges,
// because BFF responses re-spread the PP.
//
// Each order entry is a coverage selector: { section } numbers an object section, or every entry
// of an array section in array order; { section, code } picks ONE entry of an array section by its
// <SECTION>_CODE — needed where one array holds coverages at different positions (Inboedel EC holds
// the always-included Mobiele elektronica 5450 AND the optional Smart Home 5470; Rechtsbijstand RP
// holds the optional dekkingen 9520..9589). Numbering is contiguous over the coverages actually
// present, so the relative order holds regardless of which optional coverages were selected.

type CoverageSelector = { section: string; code?: string };

// Personenauto: WA (main) -> Casco -> the on-screen optional coverages.
const AUTO_ORDER: CoverageSelector[] = [
  { section: 'WA' }, { section: 'CA' }, { section: 'RP' }, { section: 'SV' },
  { section: 'PH' }, { section: 'CG' },
];

// Inboedel: main, then the two always-included hidden coverages, then glas (optional), then the
// on-screen extras. Glass therefore lands on 4 (not 2) and is skipped when not selected (e.g. when
// Woonhuis is also taken — Woonhuis already includes glass).
const INBOEDEL_ORDER: CoverageSelector[] = [
  { section: 'IP' },               // 1  Inboedel (main)
  { section: 'BI' },               // 2  Buitenhuis (always included)
  { section: 'EC', code: '5450' }, // 3  Mobiele elektronica (always included)
  { section: 'GP' },               // 4  Glas (optional)
  { section: 'LS' },               // 5  Lijfsieraden
  { section: 'EC', code: '5470' }, // 6  Smart Home
  { section: 'OP' },               // 7  Eigenaarsbelang
  { section: 'HU' },               // 8  Huurdersbelang
  { section: 'KB' },               // 9  Bijzondere bezittingen
];

// Woonhuis: Opstal (main) then glas (always included).
const WOONHUIS_ORDER: CoverageSelector[] = [
  { section: 'OP' },               // 1  Opstal (main)
  { section: 'GP' },               // 2  Glas (always included)
];

// Ongevallen: a single OD coverage section.
const ONGEVALLEN_ORDER: CoverageSelector[] = [
  { section: 'OD' },               // 1  Ongevallen (main, only coverage)
];

// Rechtsbijstand: Consument & Wonen (main, WC) then the on-screen optional dekkingen — the first
// three are their own sections, the rest are RP array entries keyed by RP_CODE.
const RECHTSBIJSTAND_ORDER: CoverageSelector[] = [
  { section: 'WC' },               // 1  Consument & Wonen (main)
  { section: 'VK' },               // 2  Verkeer
  { section: 'IN' },               // 3  Inkomen
  { section: 'FV' },               // 4  Fiscaal & Vermogen
  { section: 'RP', code: '9520' }, // 5  Eigendom & Verhuur Onroerende Zaken
  { section: 'RP', code: '9530' }, // 6  Statutair directeur
  { section: 'RP', code: '9531' }, // 7  Bestuurder Vereniging van Eigenaars
  { section: 'RP', code: '9572' }, // 8  Niet-consumentenkoop
  { section: 'RP', code: '9589' }, // 9  Financieel onvermogen
];

/** Assign <SECTION>_VOLGNUM across a PP's coverages in the given order. Mutates and returns pp. */
const assignVolgnum = (pp: any, order: CoverageSelector[]): any => {
  if (!pp || typeof pp !== 'object') return pp;
  let seq = 1;
  for (const { section, code } of order) {
    const node = pp[section];
    if (!node) continue;
    const field = `${section}_VOLGNUM`;
    if (Array.isArray(node)) {
      if (code) {
        const entry = node.find((e: any) => e && String(e[`${section}_CODE`]) === code);
        if (entry) entry[field] = seq++;
      } else {
        node.forEach((e: any) => { if (e && typeof e === 'object') e[field] = seq++; });
      }
    } else if (typeof node === 'object') {
      node[field] = seq++;
    }
  }
  return pp;
};

/** Detect which SAA product a PP belongs to by a signature coverage section. */
const orderForPp = (pp: any): CoverageSelector[] | null => {
  if (!pp || typeof pp !== 'object') return null;
  if (pp.OB) return AUTO_ORDER;           // personenauto — vehicle object
  if (pp.IP) return INBOEDEL_ORDER;       // inboedel (checked before OP — Inboedel also has OP)
  if (pp.WC) return RECHTSBIJSTAND_ORDER; // rechtsbijstand
  if (pp.OP) return WOONHUIS_ORDER;       // woonhuis — main Opstal section (Inboedel's OP already ruled out via IP)
  if (pp.OD && !pp.DR) return ONGEVALLEN_ORDER; // ongevallen — bare OD (DR carries its own DR section)
  return null;
};

/** Personenauto-only helper, used by the screen calc (operates on a single PP). */
export const orderAutoCoverages = (pp: any): any => assignVolgnum(pp, AUTO_ORDER);

/**
 * Assign coverage VOLGNUM to every supported product contract in a calculation/request document.
 * Handles both the flat Contract[] shape and the Pakket[0].Onderdeel package shape; each contract
 * is routed to its product order by its signature section. Mutates and returns the document.
 */
export const orderSaaCoveragesInDocument = (doc: any): any => {
  for (const list of [doc?.Contract, doc?.Pakket?.[0]?.Onderdeel]) {
    if (Array.isArray(list)) {
      list.forEach((c: any) => {
        const order = orderForPp(c?.PP);
        if (order) assignVolgnum(c.PP, order);
      });
    }
  }
  return doc;
};
